/**
 * Contact Form Modal Component
 * Modal for creating/editing company contacts
 */

import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  CircularProgress,
  Alert,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  CompanyContact,
  CompanyContactFormData,
  ContactType,
  CONTACT_TYPE_LABELS,
  formatPhone,
} from '../../../../types/company';

// Form validation schema
const contactSchema = yup.object({
  contactType: yup
    .string()
    .required('Tipo de contato é obrigatório')
    .oneOf(Object.values(ContactType), 'Tipo inválido'),
  name: yup
    .string()
    .required('Nome é obrigatório')
    .min(2, 'Nome deve ter pelo menos 2 caracteres'),
  email: yup
    .string()
    .email('Email inválido')
    .notRequired(),
  phone: yup.string().notRequired(),
});

interface ContactFormModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: CompanyContactFormData) => Promise<void>;
  contact?: CompanyContact;
  loading?: boolean;
  error?: string | null;
}

const ContactFormModal: React.FC<ContactFormModalProps> = ({
  open,
  onClose,
  onSubmit,
  contact,
  loading = false,
  error,
}) => {
  const [submitLoading, setSubmitLoading] = useState(false);
  const isEditing = !!contact;

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CompanyContactFormData>({
    resolver: yupResolver(contactSchema) as any,
    defaultValues: {
      contactType: '',
      name: '',
      email: '',
      phone: '',
    },
  });

  // Reset form when modal opens/closes or contact changes
  useEffect(() => {
    if (open && contact) {
      reset({
        contactType: contact.contactType,
        name: contact.name ?? '',
        email: contact.email ?? '',
        phone: contact.phone ? formatPhone(contact.phone) : '',
      });
    } else if (open && !contact) {
      reset({
        contactType: '',
        name: '',
        email: '',
        phone: '',
      });
    }
  }, [open, contact, reset]);

  const handleFormSubmit = async (data: CompanyContactFormData) => {
    setSubmitLoading(true);
    try {
      await onSubmit(data);
      onClose();
    } catch (err) {
      // Error handling is done by parent component
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleClose = () => {
    if (!submitLoading) {
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {isEditing ? 'Editar Contato' : 'Novo Contato'}
      </DialogTitle>
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Grid container spacing={2}>
            {/* Contact Type */}
            <Grid size={12}>
              <Controller
                name="contactType"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth error={!!errors.contactType}>
                    <InputLabel>Tipo de Contato *</InputLabel>
                    <Select {...field} label="Tipo de Contato *">
                      <MenuItem value="">Selecione...</MenuItem>
                      {Object.entries(CONTACT_TYPE_LABELS).map(([value, label]) => (
                        <MenuItem key={value} value={value}>
                          {label}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.contactType && (
                      <FormHelperText>{errors.contactType.message}</FormHelperText>
                    )}
                  </FormControl>
                )}
              />
            </Grid>

            {/* Name */}
            <Grid size={12}>
              <Controller
                name="name"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Nome *"
                    error={!!errors.name}
                    helperText={errors.name?.message}
                  />
                )}
              />
            </Grid>

            {/* Email */}
            <Grid
              size={{
                xs: 12,
                md: 6
              }}>
              <Controller
                name="email"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    type="email"
                    label="Email"
                    error={!!errors.email}
                    helperText={errors.email?.message}
                  />
                )}
              />
            </Grid>

            {/* Phone */}
            <Grid
              size={{
                xs: 12,
                md: 6
              }}>
              <Controller
                name="phone"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Telefone"
                    placeholder="(XX) XXXXX-XXXX"
                    error={!!errors.phone}
                    helperText={errors.phone?.message}
                    onChange={(e) => {
                      const formatted = formatPhone(e.target.value);
                      field.onChange(formatted);
                    }}
                    inputProps={{ maxLength: 15 }}
                  />
                )}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} disabled={submitLoading || loading}>
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={submitLoading || loading}
          >
            {submitLoading || loading ? (
              <CircularProgress size={24} />
            ) : isEditing ? (
              'Salvar'
            ) : (
              'Adicionar'
            )}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ContactFormModal;
